import type { ReactNode } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { ASC, DESC } from "app/components/React/Table/Utils/sort";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { WithAnimationContainer } from "app/UtilitiesAndConstants/UtilitiesComponents/WithAnimationContainer";

import { useKeywordListComposerContext } from "./KeywordListComposerContext";
import type { OrderBy } from "./types";

type HeaderItemProps = {
    field: OrderBy["field"];
    sortDirection?: OrderBy["sortDirection"];
    children: ReactNode;
    onClick: (orderBy: OrderBy) => void;
};

const HeaderItem = ({ field, sortDirection, onClick, children }: HeaderItemProps) => {
    const iconName = sortDirection === ASC ? "arrow-up" : "arrow-down";

    const handleClick = () => {
        onClick({ field, sortDirection: sortDirection === ASC ? DESC : ASC });
    };

    return (
        <Flex
            flexDirection={field === "term" ? "row-reverse" : "row"}
            gap="6px"
            alignItems="center"
            onClick={handleClick}
        >
            {sortDirection !== undefined ? (
                <WithAnimationContainer key={iconName}>
                    <SWReactIcons
                        iconColor={colorsPalettes.sky[500]}
                        iconName={iconName}
                        size="xs"
                    />
                </WithAnimationContainer>
            ) : null}
            <Text
                fontVariant="caption"
                color={colorsPalettes.carbon[250]}
            >
                {children}
            </Text>
        </Flex>
    );
};

export const KeywordListComposerCreatorBodyHeader = () => {
    const t = useTranslation();
    const { keywords, orderBy, changeSorting } = useKeywordListComposerContext();

    return (
        <Flex
            alignItems="center"
            justifyContent="space-between"
            paddingX="48px"
            paddingY="14px"
        >
            <HeaderItem
                field="term"
                sortDirection={orderBy.field === "term" ? orderBy.sortDirection : undefined}
                onClick={changeSorting}
            >
                {t("auto.keyword.list.generator.body.list.headers.term", {
                    count: keywords.length,
                })}
            </HeaderItem>
            <HeaderItem
                field="volume"
                sortDirection={orderBy.field === "volume" ? orderBy.sortDirection : undefined}
                onClick={changeSorting}
            >
                {t("auto.keyword.list.generator.body.list.headers.volume")}
            </HeaderItem>
        </Flex>
    );
};
